/**
 * Pure helpers for DROPDOWN tiles: option de-duplication / ordering and the
 * "style keys" that travel from the tile's Format popup into the widget.
 * Shared by the dropdown step (Generate) and the shell (restore / recompute).
 */
import type { LabelValueOption } from '../../shared/self-service-config.types';

export type DropdownOptionOrderMode = 'response' | 'asc' | 'desc' | 'custom';

/** A default filter's operators (Filter column / Operator / Value), as a table column has them. */
export const STANDARD_FILTER_OPERATORS: LabelValueOption[] = [
  { label: 'Equals', value: 'eq' },
  { label: 'Not equals', value: 'neq' },
  { label: 'Contains', value: 'contains' },
  { label: 'In (comma-separated list)', value: 'in' },
];

/** Default-filter operators that need no value. */
export const EMPTY_FILTER_OPERATORS: LabelValueOption[] = [
  { label: 'Is not empty', value: 'not_empty' },
  { label: 'Is empty', value: 'empty' },
];

/** `base` (a configured list, else the standard one) plus "is (not) empty". */
export function withEmptyFilterOperators(base?: LabelValueOption[] | null): LabelValueOption[] {
  const list = base?.length ? base : STANDARD_FILTER_OPERATORS;
  return [...list, ...EMPTY_FILTER_OPERATORS.filter((option) => !list.some((existing) => existing.value === option.value))];
}

/** Format keys that the dropdown widget reads straight from its widgetData. */
export const DROPDOWN_STYLE_KEYS = [
  'pillBg', 'pillText', 'pillBorder', 'pillActiveBg', 'pillActiveText', 'pillRadius', 'pillFontSize', 'pillFontFamily',
  'showLabel', 'labelColor', 'labelFontSize', 'labelFontFamily',
  'filterEnabled', 'filterDisabled',
  'mandatory',
] as const;

/** Copy the style keys present in `format` onto `widgetData` (and drop the absent ones). */
export function applyDropdownStyleKeys(format: Record<string, unknown>, widgetData: Record<string, unknown>): void {
  DROPDOWN_STYLE_KEYS.forEach((key) => {
    const value = format[key];
    if (value !== undefined && value !== null && value !== '') widgetData[key] = value;
    else delete widgetData[key];
  });
}

export function uniqueDropdownOptions(values: unknown[]): string[] {
  return [...new Set(
    values
      .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
      .map((value) => String(value)),
  )];
}

/** The typed (static) options first, then every response option not already listed. */
export function withStaticOptions(staticOptions: unknown[], responseOptions: unknown[]): string[] {
  return uniqueDropdownOptions([...(staticOptions ?? []), ...(responseOptions ?? [])]);
}

/** The typed (static) options saved with a dropdown (`payload.staticOptions`). */
export function staticDropdownOptions(payload: Record<string, unknown> | null | undefined): string[] {
  const saved = payload?.['staticOptions'];
  return Array.isArray(saved) ? uniqueDropdownOptions(saved) : [];
}

/**
 * The options an automatic pick (first value, only option) chooses from: the
 * data's values, never a typed one - typed options are only offered for the
 * user to pick.
 */
export function pickableDropdownOptions(options: unknown[], payload: Record<string, unknown> | null | undefined): string[] {
  const typed = new Set(staticDropdownOptions(payload));
  return uniqueDropdownOptions(options ?? []).filter((option) => !typed.has(option));
}

export function distinctDropdownOptions(rows: Array<Record<string, unknown>>, field: string): string[] {
  return uniqueDropdownOptions((rows || []).map((row) => row?.[field]));
}

function dropdownRowsFromComputeResponse(source: unknown): Array<Record<string, unknown>> | null {
  if (!Array.isArray(source)) return null;
  if (!source.length) return [];
  if (source.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
    const rows = source as Array<Record<string, unknown>>;
    const nested = rows.map((item) => item['data']).find(Array.isArray) as Array<Record<string, unknown>> | undefined;
    return nested ?? rows;
  }
  return null;
}

/** Option values out of a compute response: an explicit options list, else the distinct values of `field` in its rows. */
export function dropdownOptionsFromComputeResponse(response: Record<string, unknown> | null, field: string): string[] {
  const root = response ?? {};
  const result = root['result'] as Record<string, unknown> | undefined;
  const dataRecord = root['data'] as Record<string, unknown> | undefined;
  const optionArrays = [
    root['optionsList'],
    root['options'],
    root['values'],
    result?.['optionsList'],
    result?.['options'],
    dataRecord?.['optionsList'],
    dataRecord?.['options'],
  ];
  const explicit = optionArrays.find(Array.isArray) as unknown[] | undefined;
  if (explicit) return uniqueDropdownOptions(explicit);

  const rows =
    dropdownRowsFromComputeResponse(root['data']) ??
    dropdownRowsFromComputeResponse(result?.['data']) ??
    dropdownRowsFromComputeResponse(root['responseList']);
  return rows ? distinctDropdownOptions(rows, field) : [];
}

/**
 * A dropdown's values from the DATA in a compute answer: an options list, rows
 * keyed by its column, or `{ value }` rows (how an input target is answered).
 */
export function dataDropdownOptions(result: Record<string, unknown> | null, field: string): string[] {
  const listed = dropdownOptionsFromComputeResponse(result, field);
  if (listed.length) return listed;
  const rows = Array.isArray(result?.['data']) ? (result!['data'] as unknown[]) : [];
  return uniqueDropdownOptions(rows.map((row) => row && typeof row === 'object'
    ? (row as Record<string, unknown>)[field] ?? (row as Record<string, unknown>)['value']
    : row));
}

export function normalizeDropdownOptionOrderMode(value: unknown): DropdownOptionOrderMode {
  const mode = String(value ?? 'response').toLowerCase();
  return mode === 'asc' || mode === 'desc' || mode === 'custom' ? mode : 'response';
}

/** Order the (de-duplicated) options: backend order, asc/desc, or a comma-separated custom order first. */
export function orderDropdownOptions(options: string[], mode: DropdownOptionOrderMode = 'response', customOrder = ''): string[] {
  const unique = uniqueDropdownOptions(options);
  if (mode === 'asc' || mode === 'desc') {
    const sorted = [...unique].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return mode === 'desc' ? sorted.reverse() : sorted;
  }
  if (mode === 'custom') {
    const requested = uniqueDropdownOptions(String(customOrder || '').split(',').map((value) => value.trim()));
    if (!requested.length) return unique;
    const byLower = new Map(unique.map((value) => [value.toLowerCase(), value]));
    const ordered = requested
      .map((value) => byLower.get(value.toLowerCase()))
      .filter((value): value is string => !!value);
    const picked = new Set(ordered.map((value) => value.toLowerCase()));
    return [...ordered, ...unique.filter((value) => !picked.has(value.toLowerCase()))];
  }
  return unique;
}
